import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  Col, Descriptions, Row, Space, Tooltip,
} from 'antd';
import { mdiArrowRight, mdiClose } from '@mdi/js';
import IStickyBox from 'react-sticky-box';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import _ from 'lodash';

import { getAllGroupsMap, getAllServicesMap, getAllUsersMap } from '../../actors/selectors/index';
import { capitalize } from '../../mainUtils';
import { dateFormat54origins, getNameForActor } from '../../54origins/utils54origins';
import BaseTag from '../../_ui/BaseTag/BaseTag';
import BaseCard from '../../_ui/BaseCard/BaseCard';
import BaseButton from '../../_ui/BaseButton/BaseButton';
import BaseIcon from '../../_ui/BaseIcon/BaseIcon';

dayjs.extend(utc);

const HIDDEN_KEYS = new Set(['root', '_is_biom_admin', '_is_specific_service_admin']);
const MASKED_KEYS = new Set(['password']);

function parseJson(val) {
  if (!val) return {};
  if (typeof val === 'object') return val;
  try { return JSON.parse(val); } catch { return {}; }
}

function extractUinfo(parsed) {
  if (!parsed) return {};
  if (parsed.actor && parsed.actor.uinfo) return parsed.actor.uinfo;
  if (parsed.uinfo) return parsed.uinfo;
  return {};
}

function SyncLogDetailPanel({ row, onClose }) {
  const { t } = useTranslation();
  const groupsMap = useSelector(getAllGroupsMap);
  const usersMap = useSelector(getAllUsersMap);

  const params = useMemo(() => parseJson(row?.params), [row]);
  const prevParams = useMemo(() => parseJson(row?.previous_params), [row]);

  const uinfo = useMemo(() => extractUinfo(params), [params]);
  const prevUinfo = useMemo(() => extractUinfo(prevParams), [prevParams]);

  const hasPrev = Object.keys(prevUinfo).length > 0;

  const allFields = useMemo(
    () => [...new Set([...Object.keys(uinfo), ...Object.keys(prevUinfo)])].filter((k) => !HIDDEN_KEYS.has(k) && (MASKED_KEYS.has(k) ? (uinfo[k] !== prevUinfo[k]) : true)),
    [uinfo, prevUinfo],
  );

  const displayFields = useMemo(
    () => (hasPrev
      ? allFields.filter((k) => JSON.stringify(uinfo[k]) !== JSON.stringify(prevUinfo[k]))
      : allFields),
    [allFields, hasPrev, uinfo, prevUinfo],
  );

  const actorName = useMemo(() => {
    if (row?.object_name) return row.object_name;
    const uuid = row?.object_uuid;
    if (!uuid) return null;
    const actor = (usersMap instanceof Map && usersMap.get(uuid))
      || (groupsMap instanceof Map && groupsMap.get(uuid));
    if (actor) return getNameForActor(actor);
    const uinfo = Object.keys(extractUinfo(params)).length ? extractUinfo(params) : extractUinfo(prevParams);
    const fn = [uinfo.first_name, uinfo.last_name].filter(Boolean).join(' ');
    return fn || uinfo.service_name || uinfo.group_name || null;
  }, [row, usersMap, groupsMap, params]);

  if (!row) return null;

  const resolveGroupName = (uuid) => {
    if (!(groupsMap instanceof Map)) return uuid.slice(0, 8);
    const g = groupsMap.get(uuid);
    return g ? _.get(g, 'uinfo.group_name', uuid) : uuid.slice(0, 8);
  };

  const renderField = (key) => {
    let currentVal = uinfo[key] !== undefined ? uinfo[key] : null;
    let oldVal = prevUinfo[key] !== undefined ? prevUinfo[key] : null;
    let addedArr = [];
    let removedArr = [];

    if (MASKED_KEYS.has(key)) {
      oldVal = hasPrev ? '••••••••' : null;
      currentVal = capitalize(t('auth.headers.updated', 'updated'));
    } else if (key === 'date_updated') {
      oldVal = oldVal ? dayjs.utc(oldVal).local().format('YYYY-MM-DD HH:mm:ss') : '';
      currentVal = currentVal ? dayjs.utc(currentVal).local().format('YYYY-MM-DD HH:mm:ss') : '';
    } else if (key === 'groups') {
      const curr = _.isArray(currentVal) ? currentVal : [];
      const prev = _.isArray(oldVal) ? oldVal : [];
      _.difference(curr, prev).forEach((uuid) => addedArr.push(resolveGroupName(uuid)));
      _.difference(prev, curr).forEach((uuid) => removedArr.push(resolveGroupName(uuid)));
    }

    const isArrayDiff = !_.isEmpty(addedArr) || !_.isEmpty(removedArr);

    const renderScalarOrArray = (val) => {
      if (val === null || val === undefined || val === '') return null;
      if (_.isArray(val)) {
        if (val.length === 0) return null;
        return (
          <Space size={[0, 4]} direction="vertical">
            {val.map((v, i) => <div key={i}>{String(v)}</div>)}
          </Space>
        );
      }
      return <span style={{ fontSize: 12 }}>{String(val)}</span>;
    };

    return (
      <Descriptions.Item
        key={key}
        label={<span className="px-3 py-2">{key}</span>}
        labelStyle={{ width: 130 }}
        className="p-0"
      >
        <Row>
          <Col span={11} className="bg-red px-3 py-2">
            {isArrayDiff ? (
              <div className="flex flex-col">
                {removedArr.map((item, i) => (
                  <span key={`rem_${item}${i}`}>{item}</span>
                ))}
              </div>
            ) : renderScalarOrArray(oldVal)}
          </Col>
          <Col span={2} className="flex items-center justify-center">
            <BaseIcon path={mdiArrowRight} size={1} />
          </Col>
          <Col span={11} className="bg-green px-3 py-2">
            {isArrayDiff ? (
              <div className="flex flex-col">
                {removedArr.map((item, i) => (
                  <span key={`rem_${item}${i}`} className="colorRed">{`- ${item}`}</span>
                ))}
                {addedArr.map((item, i) => (
                  <span key={`add_${item}${i}`}>{`+ ${item}`}</span>
                ))}
              </div>
            ) : renderScalarOrArray(currentVal)}
          </Col>
        </Row>
      </Descriptions.Item>
    );
  };

  return (
    <IStickyBox offsetTop={16} offsetBottom={16}>
      <BaseCard loading={false}>
        <div className="flex justify-between items-start mb-4">
          <div>
            <span style={{ fontWeight: 600, fontSize: 14 }}>
              {row.action_name || row.action_class}
            </span>
            {actorName && (
              <div style={{ color: '#595959', fontSize: 12, marginTop: 2 }}>{actorName}</div>
            )}
            {row.service_name && (
              <div style={{ color: '#8c8c8c', fontSize: 12 }}>
                {`→ ${row.service_name}`}
              </div>
            )}
          </div>
          <BaseButton className="button-secondary-link" size="small" onClick={onClose}>
            <BaseIcon path={mdiClose} size={1} />
          </BaseButton>
        </div>

        <Descriptions size="small" column={1} bordered style={{ marginBottom: 16 }}>
          <Descriptions.Item label={capitalize(t('auth.headers.status', 'status'))}>
            <BaseTag className={`tag-${row.status ? 'green' : 'red'}`}>
              {row.status
                ? capitalize(t('auth.labels.delivered', 'delivered'))
                : capitalize(t('auth.labels.failed', 'failed'))}
            </BaseTag>
          </Descriptions.Item>
          <Descriptions.Item label={capitalize(t('auth.headers.created', 'created'))}>
            {row.created ? dateFormat54origins(row.created).dateWithTime : '—'}
          </Descriptions.Item>
          <Descriptions.Item label="Object">
            <Tooltip title={row.object_uuid}>
              <span>
                {actorName && <span style={{ marginRight: 6 }}>{actorName}</span>}
                <span style={{ fontFamily: 'monospace', fontSize: 11, color: '#8c8c8c' }}>
                  {row.object_uuid ? `${row.object_uuid.slice(0, 8)}…` : '—'}
                </span>
              </span>
            </Tooltip>
          </Descriptions.Item>
          <Descriptions.Item label={capitalize(t('auth.headers.by', 'by'))}>
            {row.initiated_by_name || '—'}
          </Descriptions.Item>
          {row.delivery_method && (
            <Descriptions.Item label={capitalize(t('auth.headers.delivery_method', 'method'))}>
              <BaseTag className="tag-gray-light">{row.delivery_method}</BaseTag>
            </Descriptions.Item>
          )}
        </Descriptions>

        {displayFields.length > 0 && (
          <>
            <p style={{ fontWeight: 600, fontSize: 13, marginBottom: 8 }}>
              {hasPrev
                ? capitalize(t('auth.headers.changes', 'changes'))
                : capitalize(t('auth.headers.params', 'params'))}
            </p>
            <Descriptions size="small" column={1} bordered>
              {displayFields.map(renderField)}
            </Descriptions>
          </>
        )}
      </BaseCard>
    </IStickyBox>
  );
}

SyncLogDetailPanel.propTypes = {
  row: PropTypes.object,
  onClose: PropTypes.func.isRequired,
};

export default SyncLogDetailPanel;
